import React, { useState, useMemo } from 'react'
import {
  Icon as SemanticIcon,
  IconProps as SemanticIconProps,
} from 'semantic-ui-react'
import _ from 'lodash'
import { colorVars, colorShortToLong, INHERIT } from '../../../utils/colors'

const ICON_STYLES = ['solid', 'regular', 'light', 'brand'] as const
const PREFIXES = ['fas', 'far', 'fal', 'fab'] as const

export type IconStyle = (typeof ICON_STYLES)[number]
type IconSize =
  | 'mini'
  | 'tiny'
  | 'small'
  | 'medium'
  | 'large'
  | 'big'
  | 'huge'
  | 'massive'

export interface IconProps
  extends Omit<SemanticIconProps, 'name' | 'color' | 'size'> {
  className?: string
  name: string
  color?: string
  hoverColor?: string
  iconStyle?: IconStyle
  size?: IconSize
  style?: React.CSSProperties
}

const Icon: React.FC<IconProps> = ({
  className,
  color: colorName = 'gray',
  hoverColor: hoverColorName = '',
  iconStyle = '',
  name,
  size,
  style,
  ...rest
}) => {
  const [hover, setHover] = useState(false)

  const onMouseOut = () => {
    setHover(false)
  }

  const onMouseOver = () => {
    setHover(true)
  }

  let prefix = ''
  const colorKey = useMemo(() => colorShortToLong(colorName || ''), [colorName])
  const baseColor =
    colorName === INHERIT
      ? INHERIT
      : colorVars[colorKey as keyof typeof colorVars]

  let hoverColor
  const hoverColorKey = useMemo(
    () => colorShortToLong(hoverColorName || ''),
    [hoverColorName]
  )
  if (hoverColorName === INHERIT) {
    hoverColor = INHERIT
  } else if (hoverColorName) {
    hoverColor = colorVars[hoverColorKey as keyof typeof colorVars]
  } else {
    hoverColor = baseColor
  }
  const color = hover ? hoverColor : baseColor

  let newSize = size
  let sizeOverrideStyle = {}
  if (newSize === 'medium') {
    newSize = undefined
    sizeOverrideStyle = { fontSize: '16px' }
  }

  let newName = name

  /**
   * iconStyle means we are using font-awesome pro version (not FA built into SUI-sass),
   * so we want to determine the style prefix class and add 'fa-' if necessary.
   */
  if (iconStyle) {
    const index = _.indexOf(ICON_STYLES, iconStyle)
    if (index >= 0 && index < ICON_STYLES.length) {
      prefix = PREFIXES[index]
    }
    newName = `${name.slice(0, 3) === 'fa-' ? '' : 'fa-'}${name}`
  }

  /**
   * We don't want to send name in as we'll get propType violations for names that semantic Icon
   * does not understand. Instead we'll create the classNames ourselves...
   */
  return (
    <SemanticIcon
      className={`${className || ''} ${prefix} ${newName}`}
      // @ts-ignore next
      name=''
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      size={newSize}
      style={{
        color,
        ...sizeOverrideStyle,
        ...style,
        opacity: 1,
      }}
      {...rest}
    />
  )
}

interface IconStatic extends SemanticIcon {
  defaultProps: {
    color: string
    hoverColor?: string
    iconStyle?: string
  }
  Group: typeof SemanticIcon.Group
}

const IconWithStatic = Icon as React.FC<IconProps> & IconStatic

IconWithStatic.defaultProps = {
  color: 'gray',
  hoverColor: '',
  // @ts-ignore next
  iconStyle: '',
}

IconWithStatic.Group = SemanticIcon.Group

export default Icon
